import { parseOptionsText } from "./catalogModel";
import {
  DEFAULT_ASSESSMENT_SETTINGS,
  type InstrumentDraft,
  type ModuleBuilderDraft,
  type QuestionOverride
} from "./types";

const STORAGE_KEY = "simpl-module-builder-draft";

export function newInstrumentId(): string {
  return `instr-${Math.random().toString(36).slice(2, 10)}`;
}

export function defaultInstrument(_index: number): InstrumentDraft {
  return {
    id: newInstrumentId(),
    title: "",
    description: "",
    activitiesPasteText: "",
    pasteSep: "tab",
    assessmentSettings: { ...DEFAULT_ASSESSMENT_SETTINGS }
  };
}

export function defaultDraft(): ModuleBuilderDraft {
  const first = defaultInstrument(0);
  return {
    version: 3,
    moduleTitle: "",
    moduleDescription: "",
    instruments: [first],
    activeInstrumentId: first.id,
    instrumentQuestionIdsById: { [first.id]: [] },
    instrumentQuestionRequiredById: { [first.id]: {} },
    instrumentQuestionAnswerSetById: { [first.id]: {} },
    instrumentQuestionOverridesById: { [first.id]: {} }
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function loadDraft(): ModuleBuilderDraft {
  if (typeof window === "undefined") {
    return defaultDraft();
  }
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return defaultDraft();
  }
  if (!raw) {
    return defaultDraft();
  }
  try {
    const parsed = JSON.parse(raw);
    if (!isPlainObject(parsed)) {
      return defaultDraft();
    }
    const fallback = defaultDraft();
    const draft: ModuleBuilderDraft = {
      ...fallback,
      ...(parsed as Partial<ModuleBuilderDraft>),
      version: 3,
      instruments:
        Array.isArray(parsed.instruments) && parsed.instruments.length > 0
          ? (parsed.instruments as InstrumentDraft[]).map((instr) => ({
              ...defaultInstrument(0),
              ...instr,
              assessmentSettings: {
                ...DEFAULT_ASSESSMENT_SETTINGS,
                ...(instr.assessmentSettings ?? {})
              }
            }))
          : fallback.instruments
    };
    if (!draft.instruments.find((i) => i.id === draft.activeInstrumentId)) {
      draft.activeInstrumentId = draft.instruments[0]!.id;
    }
    for (const instr of draft.instruments) {
      draft.instrumentQuestionIdsById[instr.id] ??= [];
      draft.instrumentQuestionRequiredById[instr.id] ??= {};
      draft.instrumentQuestionAnswerSetById[instr.id] ??= {};
      draft.instrumentQuestionOverridesById[instr.id] ??= {};
      // Migrate any legacy `optionsText` overrides into the structured `options`
      // shape, then drop the old field.
      const overrides = draft.instrumentQuestionOverridesById[instr.id]!;
      for (const qid of Object.keys(overrides)) {
        const ov = overrides[qid] as QuestionOverride & { optionsText?: string };
        if (ov.options === undefined && typeof ov.optionsText === "string") {
          ov.options = parseOptionsText(ov.optionsText).map((o) => ({
            label: o.label,
            value: o.value
          }));
        }
        delete ov.optionsText;
      }
    }
    return draft;
  } catch {
    return defaultDraft();
  }
}

export function saveDraft(draft: ModuleBuilderDraft): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // quota / serialization errors — ignore for now
  }
}
